import { Menu, X, Search, User, LogOut, Settings } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { UserMenu } from "@/components/auth/UserMenu";
import { Link } from "@/components/Link";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@/hooks/useNavigate";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import SearchAutocomplete from "@/components/SearchAutocomplete";
import { AddStoreModal } from "@/components/AddStoreModal";
import ContributeModal, { type ContributeAction } from "@/components/ContributeModal";
import { getMyClaims } from "@/api/merchantService";
import NotificationBell from "@/components/notifications/NotificationBell";

const NAV_LINKS = [
  { label: "الرئيسية", to: "/" },
  { label: "المتاجر", to: "/stores" },
  { label: "الفئات", to: "/categories" },
];

type HeaderProps = {
  // «شارك معنا» button in the mobile header bar (opens the contribution
  // modal). On by default on every page with the public Header; pass false
  // to opt a page out.
  showMobileContribute?: boolean;
};

const Header = ({ showMobileContribute = true }: HeaderProps = {}) => {
  const { user, loading, showAuthModal, signOut } = useAuth();
  // Moderators and admins get a shortcut to /moderator (quick 260929-j7k). isModerator comes from
  // /auth/me, derived server-side; it only shows a link, /api/moderator is guarded by moderatorAuth.
  const canModerate = !!(user?.isAdmin || user?.isModerator);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [addStoreOpen, setAddStoreOpen] = useState(false);
  const [contributeOpen, setContributeOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [desktopAutocompleteOpen, setDesktopAutocompleteOpen] = useState(false);
  const [mobileAutocompleteOpen, setMobileAutocompleteOpen] = useState(false);
  const navigate = useNavigate();

  // Merchant is DERIVED from holding an approved claim (D-06) — same query key as
  // useMerchantStores so React Query dedupes the call across header + portal.
  const { data: claimsData } = useQuery({
    queryKey: ["merchant", "my-claims"],
    queryFn: getMyClaims,
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
  const isMerchant = !!(
    claimsData?.ok && claimsData.data?.some((c) => c.status === "approved")
  );

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setDesktopAutocompleteOpen(false);
      setMobileAutocompleteOpen(false);
      navigate(`/stores?q=${encodeURIComponent(searchQuery.trim())}`);
      setIsMobileMenuOpen(false);
    }
  };

  // Add-store action — gated exactly like ContributeGrid's add-store card:
  // guests get the auth modal, logged-in users get AddStoreModal.
  const handleAddStore = () => {
    setIsMobileMenuOpen(false);
    if (!user) {
      showAuthModal({ trigger: "add_store" });
    } else {
      setAddStoreOpen(true);
    }
  };

  const handleSignOut = async () => {
    setIsMobileMenuOpen(false);
    await signOut();
  };

  // «شارك معنا» modal actions — guests are routed to the auth modal with a
  // "sign up first" note; logged-in users go straight to the real action.
  const handleContributeAction = (action: ContributeAction) => {
    setContributeOpen(false);
    if (!user) {
      showAuthModal({ trigger: "contribute", message: "سجّل الأول عشان تقدر تشارك معانا" });
      return;
    }
    if (action === "review") {
      navigate("/stores");
    } else if (action === "add-store") {
      setAddStoreOpen(true);
    } else if (action === "claim") {
      navigate("/for-business");
    }
  };

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        background: "var(--ys-paper)",
        borderBottom: "1px solid var(--ys-rule)",
        direction: "rtl",
      }}
    >
      <div
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          padding: "0 28px",
          height: 54,
          display: "flex",
          alignItems: "center",
          gap: 24,
        }}
      >
        {/* Brand */}
        <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none", flexShrink: 0 }}>
          <img src="/brand/mark-color.svg" width={30} height={30} alt="يتفرج" style={{ display: "block", flexShrink: 0 }} />
          <span style={{ fontFamily: "var(--ys-font)", fontWeight: 700, fontSize: 22, color: "var(--ys-primary)" }}>
            يتفرج
          </span>
        </Link>

        {/* Desktop Search */}
        <Popover open={desktopAutocompleteOpen} onOpenChange={setDesktopAutocompleteOpen}>
          <PopoverTrigger asChild>
            <form
              onSubmit={handleSearchSubmit}
              className="hidden md:flex"
              style={{
                flex: 1,
                maxWidth: 480,
                alignItems: "center",
                gap: 8,
                height: 36,
                border: "1px solid var(--ys-rule-2)",
                borderRadius: "var(--ys-r-pill)",
                background: "var(--ys-paper)",
                padding: "0 14px",
                transition: "border-color 180ms, box-shadow 180ms",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "var(--ys-primary)";
                e.currentTarget.style.boxShadow = "0 0 0 3px var(--ys-orange-100)";
              }}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  e.currentTarget.style.borderColor = "var(--ys-rule-2)";
                  e.currentTarget.style.boxShadow = "none";
                }
              }}
            >
              <Search style={{ width: 14, height: 14, color: "var(--ys-ink-3)", flexShrink: 0 }} />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setDesktopAutocompleteOpen(e.target.value.trim().length >= 2);
                }}
                placeholder="دوّر على متجر أو فئة…"
                style={{
                  flex: 1,
                  border: "none",
                  background: "transparent",
                  outline: "none",
                  fontFamily: "var(--ys-font)",
                  fontSize: 13,
                  color: "var(--ys-ink)",
                  direction: "rtl",
                }}
              />
            </form>
          </PopoverTrigger>
          <SearchAutocomplete
            inputValue={searchQuery}
            open={desktopAutocompleteOpen}
            onClose={() => setDesktopAutocompleteOpen(false)}
          />
        </Popover>

        {/* Desktop Nav */}
        <nav
          className="hidden md:flex"
          style={{ alignItems: "center", gap: 22, marginRight: "auto" }}
        >
          {NAV_LINKS.map((link) => (
            <NavLink key={link.label} to={link.to}>
              {link.label}
            </NavLink>
          ))}
          <button
            onClick={handleAddStore}
            style={{
              fontFamily: "var(--ys-font)",
              fontSize: 13,
              color: "var(--ys-ink-2)",
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            ضيف متجر
          </button>
          {isMerchant && (
            <Link
              to="/merchant"
              style={{
                fontFamily: "var(--ys-font)",
                fontSize: 13,
                fontWeight: 600,
                color: "var(--ys-primary)",
                textDecoration: "none",
              }}
            >
              لوحة المتجر
            </Link>
          )}
          {canModerate && (
            <a
              href="/moderator"
              style={{
                fontFamily: "var(--ys-font)",
                fontSize: 13,
                fontWeight: 600,
                color: "var(--ys-primary)",
                textDecoration: "none",
              }}
            >
              لوحة المشرف
            </a>
          )}
          {user?.isAdmin && (
            <a
              href="/admin"
              style={{
                fontFamily: "var(--ys-font)",
                fontSize: 13,
                color: "var(--ys-ink-2)",
                textDecoration: "none",
              }}
            >
              Admin
            </a>
          )}
        </nav>

        {/* Desktop Auth CTAs */}
        <div
          className="hidden md:flex"
          style={{ alignItems: "center", gap: 8, flexShrink: 0 }}
        >
          {loading ? (
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "var(--ys-bg-2)",
              }}
            />
          ) : user ? (
            <>
              <NotificationBell placement="desktop" />
              <UserMenu />
            </>
          ) : (
            <AuthBtn onClick={() => showAuthModal({ trigger: "nav" })} variant="primary">دخول / تسجيل</AuthBtn>
          )}
        </div>

        {/* Mobile contribute CTA — visible in the bar itself (not just the drawer) */}
        {showMobileContribute && (
          <div
            className="flex md:hidden"
            style={{ alignItems: "center", gap: 8, marginRight: "auto", flexShrink: 0 }}
          >
            {canModerate ? (
              <ModeratorLinkBtn />
            ) : (
              <AuthBtn onClick={() => setContributeOpen(true)} variant="primary">
                شارك معنا
              </AuthBtn>
            )}
          </div>
        )}

        {/* Mobile bell: renders only below md for signed-in users (see NotificationBell). The
            negative inline margin tightens the bar gap to the hamburger to 8px so 360px fits. */}
        {user && (
          <div
            className="flex md:hidden"
            style={{ alignItems: "center", marginRight: showMobileContribute ? 0 : "auto", marginLeft: -16, flexShrink: 0 }}
          >
            <NotificationBell placement="mobile" />
          </div>
        )}

        {/* Mobile hamburger — pushed to end (left in RTL) */}
        <button
          className="flex md:hidden items-center justify-center"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          style={{
            marginRight: showMobileContribute || user ? 0 : "auto",
            width: 36,
            height: 36,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "var(--ys-ink)",
            borderRadius: "var(--ys-r-sm)",
          }}
          aria-label="القائمة"
        >
          {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {isMobileMenuOpen && (
        <div
          style={{
            background: "var(--ys-paper)",
            borderTop: "1px solid var(--ys-rule)",
            direction: "rtl",
          }}
        >
          {/* Mobile Search */}
          <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--ys-rule)" }}>
            <Popover open={mobileAutocompleteOpen} onOpenChange={setMobileAutocompleteOpen}>
              <PopoverTrigger asChild>
                <form
                  onSubmit={handleSearchSubmit}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    height: 38,
                    border: "1px solid var(--ys-rule-2)",
                    borderRadius: "var(--ys-r-pill)",
                    background: "var(--ys-paper)",
                    padding: "0 14px",
                  }}
                >
                  <Search style={{ width: 14, height: 14, color: "var(--ys-ink-3)", flexShrink: 0 }} />
                  <input
                    type="search"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setMobileAutocompleteOpen(e.target.value.trim().length >= 2);
                    }}
                    placeholder="دوّر على متجر أو فئة…"
                    style={{
                      flex: 1,
                      border: "none",
                      background: "transparent",
                      outline: "none",
                      fontFamily: "var(--ys-font)",
                      fontSize: 13,
                      color: "var(--ys-ink)",
                      direction: "rtl",
                    }}
                  />
                </form>
              </PopoverTrigger>
              <SearchAutocomplete
                inputValue={searchQuery}
                open={mobileAutocompleteOpen}
                onClose={() => setMobileAutocompleteOpen(false)}
              />
            </Popover>
          </div>

          {/* Mobile Nav Links */}
          <nav style={{ padding: "8px 0" }}>
            {NAV_LINKS.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  display: "block",
                  padding: "10px 16px",
                  fontFamily: "var(--ys-font)",
                  fontSize: 14,
                  color: "var(--ys-ink-2)",
                  textDecoration: "none",
                }}
              >
                {link.label}
              </Link>
            ))}
            <button
              onClick={handleAddStore}
              style={{
                display: "block",
                width: "100%",
                padding: "10px 16px",
                fontFamily: "var(--ys-font)",
                fontSize: 14,
                color: "var(--ys-ink-2)",
                background: "transparent",
                border: "none",
                textAlign: "start",
                cursor: "pointer",
              }}
            >
              ضيف متجر
            </button>
            {isMerchant && (
              <Link
                to="/merchant"
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  display: "block",
                  padding: "10px 16px",
                  fontFamily: "var(--ys-font)",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "var(--ys-primary)",
                  textDecoration: "none",
                }}
              >
                لوحة المتجر
              </Link>
            )}
            {canModerate && (
              <a
                href="/moderator"
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  display: "block",
                  padding: "10px 16px",
                  fontFamily: "var(--ys-font)",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "var(--ys-primary)",
                  textDecoration: "none",
                }}
              >
                لوحة المشرف
              </a>
            )}
            {user?.isAdmin && (
              <a
                href="/admin"
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  display: "block",
                  padding: "10px 16px",
                  fontFamily: "var(--ys-font)",
                  fontSize: 14,
                  color: "var(--ys-ink-2)",
                  textDecoration: "none",
                }}
              >
                Admin
              </a>
            )}
          </nav>

          {/* Mobile Auth CTAs */}
          {!loading && !user && (
            <div
              style={{
                padding: "12px 16px 16px",
                borderTop: "1px solid var(--ys-rule)",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <button
                onClick={() => { showAuthModal({ trigger: "nav" }); setIsMobileMenuOpen(false); }}
                style={{
                  fontFamily: "var(--ys-font)",
                  fontSize: 14,
                  fontWeight: 600,
                  padding: "10px 14px",
                  border: "1px solid var(--ys-primary)",
                  borderRadius: "var(--ys-r)",
                  background: "var(--ys-primary)",
                  color: "#fff",
                  cursor: "pointer",
                  width: "100%",
                }}
              >
                دخول / تسجيل
              </button>
            </div>
          )}

          {/* Mobile Auth — logged in */}
          {!loading && user && (
            <div
              style={{
                padding: "12px 16px 16px",
                borderTop: "1px solid var(--ys-rule)",
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
            >
              <div style={{ padding: "4px 4px 10px" }}>
                <p
                  style={{
                    margin: 0,
                    fontFamily: "var(--ys-font)",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--ys-ink)",
                  }}
                >
                  {user.full_name || "المستخدم"}
                </p>
                {user.email && (
                  <p
                    style={{
                      margin: "2px 0 0",
                      fontFamily: "var(--ys-font)",
                      fontSize: 12,
                      color: "var(--ys-ink-3)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {user.email}
                  </p>
                )}
              </div>
              <Link
                to={`/u/${user._id}`}
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 4px",
                  fontFamily: "var(--ys-font)",
                  fontSize: 14,
                  color: "var(--ys-ink-2)",
                  textDecoration: "none",
                }}
              >
                <User size={16} />
                الملف الشخصي
              </Link>
              <Link
                to="/profile"
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 4px",
                  fontFamily: "var(--ys-font)",
                  fontSize: 14,
                  color: "var(--ys-ink-2)",
                  textDecoration: "none",
                }}
              >
                <Settings size={16} />
                الإعدادات
              </Link>
              <button
                onClick={handleSignOut}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  padding: "10px 4px",
                  fontFamily: "var(--ys-font)",
                  fontSize: 14,
                  color: "var(--ys-danger, #d64545)",
                  background: "transparent",
                  border: "none",
                  textAlign: "start",
                  cursor: "pointer",
                }}
              >
                <LogOut size={16} />
                تسجيل الخروج
              </button>
            </div>
          )}
        </div>
      )}

      <AddStoreModal isOpen={addStoreOpen} onClose={() => setAddStoreOpen(false)} />
      {showMobileContribute && (
        <ContributeModal
          isOpen={contributeOpen}
          onClose={() => setContributeOpen(false)}
          onAction={handleContributeAction}
        />
      )}
    </header>
  );
};

// ─── Small helpers ────────────────────────────────────────────────────────────

function NavLink({ to, children }: { to: string; children: React.ReactNode }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={to}
      style={{
        fontFamily: "var(--ys-font)",
        fontSize: 13,
        color: hovered ? "var(--ys-ink)" : "var(--ys-ink-2)",
        textDecoration: "none",
        transition: "color 150ms",
        whiteSpace: "nowrap",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </Link>
  );
}

// Staff shortcut to the moderator PWA (quick 260929-j7k). A full navigation (<a>, like Admin)
// because /moderator is its own client-only PWA scope. No PostHog event: staff-only (D-12 spirit).
function ModeratorLinkBtn() {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href="/moderator"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        fontFamily: "var(--ys-font)",
        fontSize: 12,
        fontWeight: 600,
        padding: "7px 14px",
        border: "1px solid var(--ys-primary)",
        borderRadius: "var(--ys-r)",
        background: hovered ? "var(--ys-primary-hover)" : "var(--ys-primary)",
        color: "#fff",
        textDecoration: "none",
        transition: "background 150ms, border-color 150ms",
        whiteSpace: "nowrap",
      }}
    >
      لوحة المشرف
    </a>
  );
}

function AuthBtn({
  onClick,
  variant,
  children,
}: {
  onClick: () => void;
  variant: "primary" | "secondary";
  children: React.ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  const isPrimary = variant === "primary";
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        fontFamily: "var(--ys-font)",
        fontSize: 12,
        fontWeight: 600,
        padding: "7px 14px",
        border: isPrimary
          ? "1px solid var(--ys-primary)"
          : "1px solid var(--ys-rule-2)",
        borderRadius: "var(--ys-r)",
        background: isPrimary
          ? hovered ? "var(--ys-primary-hover)" : "var(--ys-primary)"
          : hovered ? "var(--ys-bg-2)" : "var(--ys-paper)",
        color: isPrimary ? "#fff" : "var(--ys-ink)",
        cursor: "pointer",
        transition: "background 150ms, border-color 150ms",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

export default Header;
